/**
 * The composition root's context: the store, the sync engine, the API client,
 * and who is holding the phone.
 *
 * These three objects are built once in src/main.tsx and never rebuilt. A
 * screen never imports Dexie, never constructs a `SurveyStore`, and never calls
 * `fetch` — it asks for what it needs here.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { AdminClient } from '../../db/admin-client';
import type { ApiClient } from '../../db/http-transport';
import type { SurveyStore } from '../../db/store';
import type { SyncEngine } from '../../db/sync';
import type { LogSink } from '../../core/log';
import { readCachedIdentity, writeCachedIdentity, type Identity } from './identity';

export interface AppServices {
  store: SurveyStore;
  sync: SyncEngine;
  api: ApiClient;
  /**
   * The office's client for the `ADMIN_TOKEN` routes. Separate from `api` on
   * purpose: the two hold different credentials for different principals, and
   * one client would make it a one-line edit to send the wrong one.
   */
  admin: AdminClient;
  /** Whether a surveyor token is present at all. False = the app is unlinked. */
  linked: boolean;
  /**
   * Where structured log lines go — `src/core/log.ts`: "The sink is injected
   * everywhere it is used." `main.tsx` wires `consoleLogSink`; a test wires a
   * collector and asserts on the lines. Nothing under `src/ui` may import
   * `consoleLogSink` directly — this is the one door.
   */
  log: LogSink;
}

export interface ServicesValue extends AppServices {
  /** From `/api/me`, or from the offline cache, or null on an unlinked device. */
  identity: Identity | null;
}

const ServicesContext = createContext<ServicesValue | null>(null);

export interface ServicesProviderProps {
  services: AppServices;
  children: ReactNode;
}

export function ServicesProvider({ services, children }: ServicesProviderProps): ReactNode {
  // Seeded from the cache so a cold offline launch already knows who it is.
  const [identity, setIdentity] = useState<Identity | null>(() => readCachedIdentity());

  useEffect(() => {
    if (!services.linked) return;
    let cancelled = false;
    void services.api
      .me()
      .then((fresh) => {
        if (cancelled) return;
        writeCachedIdentity(fresh);
        setIdentity(fresh);
      })
      // Offline, or an expired token. The cached identity stands; the sync pill
      // is where connectivity is reported, not here.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [services]);

  const value = useMemo<ServicesValue>(() => ({ ...services, identity }), [services, identity]);

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServices(): ServicesValue {
  const value = useContext(ServicesContext);
  if (!value) throw new Error('useServices must be used inside <ServicesProvider>');
  return value;
}
